/**
 * components/legal/companyDetails.ts
 *
 * The provider's identification, as published on the public legal pages.
 *
 * This is the **single place** to edit the company's public facts. The payment
 * gateway (GoPay) verifies that a merchant site carries a complete
 * identification — company name, company ID, full address — plus a publicly
 * visible e-mail and phone number, so every one of those fields must be
 * filled before the portal review.
 *
 * A fact that is not yet known ships as a loud `[ DOPLNIT / TO FILL — … ]`
 * marker rather than a plausible-looking guess: a wrong seat address on a tax
 * document or a merchant application is worse than an obviously unfinished
 * one. None is left since the phone number was filled (29 Sep 2026), and
 * `legalContent.test.ts` fails a document that carries one.
 *
 * Keep in sync with the backend supplier identity used on invoices
 * (`optivo-service/src/services/billing/billingTax.ts` and the
 * `BILLING_SUPPLIER_*` / `BILLING_BANK_*` environment variables) — these are
 * the same real-world facts rendered on two surfaces.
 */

export const COMPANY = {
    /** Trading name used across the product. */
    brandName: "Optivo",
    /** Registered legal name, as in the commercial register. */
    legalName: "OPTIVO s.r.o.",
    /** IČO — Czech company identification number. */
    ico: "24644986",
    /**
     * DIČ — Czech VAT ID. `null` while the company is not a VAT payer, which
     * is also the backend default (`BILLING_VAT_PAYER` is off) and what the
     * register shows: the entry carries an IČO and no DIČ. Setting one here
     * means setting `BILLING_VAT_PAYER=true` server-side and revisiting the
     * price wording in `legalContent.ts`.
     */
    dic: null as string | null,
    /**
     * Registered seat, as entered in the commercial register: obec Tisá, část
     * obce Tisá, ulice Ostrov, adresní místo Ostrov 12. "Ostrov" is the street
     * here, not a separate village — the whole address belongs to Tisá.
     */
    street: "Ostrov 12",
    city: "Tisá",
    zip: "403 36",
    country: "Česká republika",
    countryEn: "Czech Republic",
    /**
     * Commercial-register entry (spisová značka + court), cited in Czech in
     * both languages because it is a legal identifier, not a phrase to
     * translate. Register lists it as `C 55655/KSUL`.
     */
    registration: "C 55655 vedená u Krajského soudu v Ústí nad Labem",
    /**
     * The company's official address — the one every legal document names:
     * the identification in the terms and on /contact, where a complaint, a
     * withdrawal, a cancelled mandate or a data-protection request goes, and
     * the footers of the legal pages and the welcome page, which is where the
     * gateway's reviewer reads it. Settled in the review of the terms on
     * 29 Sep 2026, which moved the documents off `supportEmail`: the set has
     * to name one address, or article 9 of the terms sends a complaint to one
     * mailbox and the complaints procedure it points at to another.
     */
    email: "info@optivo.cz",
    /**
     * Where product questions go — the address the app names where an "Ask
     * Optivo" request cannot be sent (via `support/support-contact.ts`).
     * No legal document names it; `legalContent.test.ts` holds that.
     */
    supportEmail: "support@optivo.cz",
    /**
     * Where invoice questions go, on /contact beside the bank details — today
     * the same mailbox as `email`. All three are role mailboxes rather than a
     * person's address, which is what survives someone leaving, but only while
     * they are actually delivered and monitored.
     */
    billingEmail: "info@optivo.cz",
    /**
     * How long a customer waits for an answer at `supportEmail`, in hours —
     * the response promise the product makes on every screen that invites a
     * question. It lives here rather than in the copy because it is a public
     * commitment like the address beside it, and because two locales typing
     * "24" into their own sentence is two places for one number to drift; the
     * `support.replyWithin` phrase templates `{hours}` instead.
     *
     * Note this is a *response* time, not the settlement deadline the
     * complaints procedure states — see `legalContent.ts`, which keeps the
     * statutory 30 days for a formal reklamace.
     */
    supportResponseHours: 24,
    /**
     * Publicly visible phone number. GoPay's checklist asks for a company
     * e-mail *and* a company phone, and the commercial register publishes
     * neither, so this one was decided rather than looked up (review of the
     * terms, 29 Sep 2026). It renders on /contact, in the terms and in both
     * public footers.
     */
    phone: "+420 475 222 428",
    /** Support hours, shown next to the contact details. */
    supportHoursCs: "pracovní dny 9:00–17:00",
    supportHoursEn: "business days 9:00–17:00",
    website: "https://optivo.cz",
    /** Wire-transfer account (mirrors `BILLING_BANK_*` on the server). */
    bankName: "Československá obchodní banka, a. s. (ČSOB)",
    bankAccount: "375747901/0300",
    iban: "CZ55 0300 0000 0003 7574 7901",
    bic: "CEKOCZPP",
} as const;

/** `Street, ZIP City, Country` on one line. */
export function formattedAddress(lang: "cs" | "en"): string {
    const country = lang === "cs" ? COMPANY.country : COMPANY.countryEn;
    return `${COMPANY.street}, ${COMPANY.zip} ${COMPANY.city}, ${country}`;
}
